import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  TrendingUp, TrendingDown, DollarSign, Bus, Route, 
  Calendar, Download, RefreshCw, BarChart3, PieChart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ReportsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [dateFrom, setDateFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [dateTo, setDateTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedRoute, setSelectedRoute] = useState('');
  const [selectedBus, setSelectedBus] = useState('');

  useEffect(() => {
    if (!user || !['station_superuser', 'overseer'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchFilters();
  }, [user, navigate]);

  const fetchFilters = async () => {
    try {
      const [routesRes, busesRes] = await Promise.all([
        axios.get(`${API_URL}/routes`),
        axios.get(`${API_URL}/buses`)
      ]);
      setRoutes(routesRes.data);
      setBuses(busesRes.data);
    } catch (error) {
      console.error('Failed to load filters');
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        date_from: new Date(dateFrom).toISOString(),
        date_to: new Date(dateTo + 'T23:59:59').toISOString()
      });
      
      if (selectedRoute) params.append('route_id', selectedRoute);
      if (selectedBus) params.append('bus_id', selectedBus);
      
      const response = await axios.get(`${API_URL}/reports/pnl?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!report) return;
    
    let csv = 'Panashe Coaches P&L Report\n';
    csv += `Period: ${dateFrom} to ${dateTo}\n\n`;
    csv += 'SUMMARY\n';
    csv += `Total Revenue (USD),${report.summary.total_revenue_usd}\n`;
    csv += `Total Revenue (ZAR),${report.summary.total_revenue_zar}\n`;
    csv += `Total Expenses (USD),${report.summary.total_expenses_usd}\n`;
    csv += `Gross Profit (USD),${report.summary.gross_profit_usd}\n`;
    csv += `Profit Margin,${report.summary.profit_margin_percent}%\n\n`;
    
    csv += 'REVENUE BY ROUTE\n';
    csv += 'Route,USD,ZAR,Bookings\n';
    report.revenue_by_route.forEach(r => {
      csv += `${r.route},${r.usd},${r.zar},${r.bookings}\n`;
    });
    
    csv += '\nEXPENSES BY CATEGORY\n';
    csv += 'Category,Amount (USD),Count\n';
    report.expenses_by_category.forEach(e => {
      csv += `${e.category},${e.amount_usd},${e.count}\n`;
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pnl_report_${dateFrom}_${dateTo}.csv`;
    a.click();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold">P&L Financial Reports</h1>
            <p className="text-slate-500">Profit and Loss analysis with custom date range</p>
          </div>
          {report && (
            <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="date-from"
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="date-to"
                />
              </div>
              <div>
                <Label>Route (optional)</Label>
                <Select value={selectedRoute || 'all'} onValueChange={(v) => setSelectedRoute(v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All routes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Routes</SelectItem>
                    {routes.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bus (optional)</Label>
                <Select value={selectedBus || 'all'} onValueChange={(v) => setSelectedBus(v === 'all' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All buses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Buses</SelectItem>
                    {buses.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.registration_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="btn-panashe" 
                onClick={fetchReport}
                disabled={loading}
                data-testid="generate-report-btn"
              >
                {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <BarChart3 className="h-4 w-4 mr-2" />}
                Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Report Results */}
        {report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-600">${report.summary.total_revenue_usd_equivalent.toLocaleString()}</p>
                      <p className="text-xs text-slate-400 mt-1">USD: ${report.summary.total_revenue_usd} | ZAR: R{report.summary.total_revenue_zar}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <DollarSign className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Expenses</p>
                      <p className="text-2xl font-bold text-red-600">${report.summary.total_expenses_usd.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-full">
                      <TrendingDown className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Gross Profit</p>
                      <p className={`text-2xl font-bold ${report.summary.gross_profit_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${report.summary.gross_profit_usd.toLocaleString()}
                      </p>
                    </div>
                    <div className={`p-3 ${report.summary.gross_profit_usd >= 0 ? 'bg-green-100' : 'bg-red-100'} rounded-full`}>
                      <TrendingUp className={`h-6 w-6 ${report.summary.gross_profit_usd >= 0 ? 'text-green-600' : 'text-red-600'}`} />
                    </div>
                  </div>
                  <div className={`flex items-center mt-2 text-sm ${report.summary.profit_margin_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {report.summary.profit_margin_percent >= 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                    {Math.abs(report.summary.profit_margin_percent)}% margin
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Bookings / Passengers</p>
                      <p className="text-2xl font-bold text-blue-600">{report.summary.total_bookings}</p>
                      <p className="text-xs text-slate-400 mt-1">{report.summary.total_passengers} passengers across {report.summary.total_trips} trips</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <Bus className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue by Route */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Route className="h-5 w-5" />
                  Revenue by Route
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.revenue_by_route.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No revenue data for this period</p>
                ) : (
                  <div className="space-y-3">
                    {report.revenue_by_route.map((route, idx) => {
                      const maxRevenue = Math.max(...report.revenue_by_route.map(r => r.usd + r.zar / report.fx_rate));
                      const percentage = ((route.usd + route.zar / report.fx_rate) / maxRevenue) * 100;
                      
                      return (
                        <div key={idx}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-medium">{route.route}</span>
                            <span className="text-sm">
                              ${route.usd.toFixed(2)} + R{route.zar.toFixed(2)} ({route.bookings} bookings)
                            </span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-panashe-blue rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Revenue by Bus */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bus className="h-5 w-5" />
                  Revenue by Bus
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.revenue_by_bus.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No bus data for this period</p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {report.revenue_by_bus.map((bus, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg">
                        <p className="font-mono font-bold text-lg">{bus.bus}</p>
                        <p className="text-sm text-slate-500">{bus.trips} trips</p>
                        <div className="mt-2">
                          <span className="text-green-600 font-medium">${bus.usd.toFixed(2)}</span>
                          {bus.zar > 0 && (
                            <span className="text-slate-500 ml-2">+ R{bus.zar.toFixed(2)}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expenses by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Expenses by Category
                </CardTitle>
              </CardHeader>
              <CardContent>
                {report.expenses_by_category.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No approved expenses for this period</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {report.expenses_by_category.map((expense, idx) => {
                      const colors = ['bg-red-100 text-red-800', 'bg-orange-100 text-orange-800', 'bg-yellow-100 text-yellow-800', 'bg-blue-100 text-blue-800', 'bg-purple-100 text-purple-800', 'bg-slate-100 text-slate-800'];
                      return (
                        <div key={idx} className={`p-4 rounded-lg ${colors[idx % colors.length]}`}>
                          <p className="text-sm font-medium capitalize">{expense.category.replace('_', ' ')}</p>
                          <p className="text-xl font-bold mt-1">${expense.amount_usd.toFixed(2)}</p>
                          <p className="text-xs opacity-75">{expense.count} entries</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Period Info */}
            <div className="text-center text-sm text-slate-500">
              <Calendar className="h-4 w-4 inline mr-1" />
              Report period: {format(new Date(report.period.from), 'dd MMM yyyy')} - {format(new Date(report.period.to), 'dd MMM yyyy')}
              <span className="mx-2">|</span>
              Exchange Rate: 1 USD = {report.fx_rate} ZAR
            </div>
          </>
        )}

        {!report && !loading && (
          <Card>
            <CardContent className="p-12 text-center text-slate-500">
              <BarChart3 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
              <p className="text-lg font-medium">Select date range and click "Generate Report"</p>
              <p className="text-sm mt-1">View revenue, expenses, and profit analysis</p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
